import { Chess, type Square } from 'chess.js';
import type {
  AnalysisResult,
  Color,
  CoachingReport,
  EngineScore,
  KeyMoment,
  MistakeSeverity,
  MistakeStats,
  ParsedGame,
} from '../../types';
import type { StockfishEngine } from '../engine/StockfishEngine';
import { scoreFromPlayerPov } from '../engine/uci';
import { computeLossCp, toComparableCp } from '../utils/evalFormat';
import { detectThemes } from './themes';
import { explainMoment } from './explain';
import { computeReplyFacts } from './punishment';

export interface AnalyzeGameOptions {
  screeningDepth?: number;
  confirmDepth?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  onProgress?: (progress: { phase: 'screening' | 'confirming'; current: number; total: number }) => void;
}

const DEFAULTS = {
  screeningDepth: 12,
  confirmDepth: 18,
  timeoutMs: 12000,
};

const BLUNDER_THRESHOLD = 200; // ~2 pions
const MISTAKE_THRESHOLD = 100;
const INACCURACY_THRESHOLD = 50;
const MIN_PLY_GAP_BETWEEN_MOMENTS = 4; // évite deux moments issus de la même séquence forcée
const MAX_MOMENTS_SHOWN = 2; // moments réellement expliqués à l'utilisateur
const MAX_MOMENTS_FOR_EXERCISES = 3; // dont un supplémentaire réservé aux exercices

function classifySeverity(lossCp: number): MistakeSeverity | null {
  if (lossCp >= BLUNDER_THRESHOLD) return 'blunder';
  if (lossCp >= MISTAKE_THRESHOLD) return 'mistake';
  if (lossCp >= INACCURACY_THRESHOLD) return 'inaccuracy';
  return null;
}

/** Position de départ effective de la partie (gère les PGN avec FEN de départ personnalisée). */
function startFen(game: ParsedGame): string {
  if (game.moves.length > 0) return game.moves[0].fenBefore;
  if (game.headers.FEN) return game.headers.FEN;
  return new Chess().fen();
}

function terminalToWhiteScore(result: AnalysisResult, fen: string): EngineScore {
  if (result.terminalReason === 'checkmate') {
    // Le camp au trait dans `fen` est celui qui est maté. On utilise 1
    // (plutôt que 0) comme distance sentinelle : en JavaScript, -0 >= 0 est
    // vrai, ce qui casserait la détection du camp gagnant si on l'utilisait.
    const sideToMove = fen.split(' ')[1] === 'b' ? 'b' : 'w';
    const winner: Color = sideToMove === 'w' ? 'b' : 'w';
    return { type: 'mate', value: winner === 'w' ? 1 : -1 };
  }
  return { type: 'cp', value: 0 }; // pat ou nulle : position équilibrée par définition
}

function pvToSan(fen: string, pvUci: string[], maxMoves = 5): { san: string[]; firstUci: string } {
  const chess = new Chess(fen);
  const sanMoves: string[] = [];
  let firstUci = '';
  for (const uci of pvUci.slice(0, maxMoves)) {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    try {
      const move = chess.move({ from: from as Square, to: to as Square, promotion });
      if (!move) break;
      if (!firstUci) firstUci = uci;
      sanMoves.push(move.san);
    } catch {
      break;
    }
  }
  return { san: sanMoves, firstUci: firstUci || pvUci[0] };
}

interface ScreenedPosition {
  fen: string;
  whiteScore: EngineScore;
  bestPvUci: string[];
  depthReached: number;
  timedOut: boolean;
}

/**
 * Analyse pédagogique complète d'une partie pour un joueur donné.
 * Deux passes : un balayage rapide de toutes les positions pour repérer
 * les candidats, puis une réanalyse plus profonde des meilleurs candidats
 * avant de les retenir définitivement (cf. exigence produit).
 */
export async function analyzeGame(
  game: ParsedGame,
  playerColor: Color,
  engine: StockfishEngine,
  options: AnalyzeGameOptions = {},
): Promise<CoachingReport> {
  const screeningDepth = options.screeningDepth ?? DEFAULTS.screeningDepth;
  const confirmDepth = options.confirmDepth ?? DEFAULTS.confirmDepth;
  const timeoutMs = options.timeoutMs ?? DEFAULTS.timeoutMs;
  const notes: string[] = [];

  if (game.moves.length === 0) {
    return {
      gameId: game.id,
      moments: [],
      momentsForExercises: [],
      analyzedAt: new Date().toISOString(),
      engineDepth: 0,
      notes: ['Cette partie ne contient aucun coup à analyser.'],
      playerColor,
      stats: { playerMoves: 0, inaccuracies: 0, mistakes: 0, blunders: 0 },
    };
  }

  const fens = [startFen(game), ...game.moves.map((m) => m.fenAfter)];

  // --- Passe 1 : balayage de toutes les positions à profondeur modérée.
  const screened: ScreenedPosition[] = [];
  let anyTimeout = false;
  for (let i = 0; i < fens.length; i++) {
    options.onProgress?.({ phase: 'screening', current: i + 1, total: fens.length });
    const fen = fens[i];
    const result = await engine.analyze(fen, { depth: screeningDepth, timeoutMs, signal: options.signal });
    if (result.isTerminal) {
      screened.push({ fen, whiteScore: terminalToWhiteScore(result, fen), bestPvUci: [], depthReached: 0, timedOut: false });
      continue;
    }
    if (result.timedOut) anyTimeout = true;
    const top = result.bestLines[0];
    screened.push({
      fen,
      whiteScore: top?.score ?? { type: 'cp', value: 0 },
      bestPvUci: top?.pvUci ?? [],
      depthReached: result.depthReached,
      timedOut: result.timedOut,
    });
  }
  if (anyTimeout) {
    notes.push(
      "Certaines positions n'ont pas atteint la profondeur d'analyse cible dans le délai imparti ; les moments retenus restent basés sur les meilleures informations disponibles.",
    );
  }

  // --- Construction des candidats (un par coup joué par le joueur étudié).
  interface Candidate {
    moveIndex: number;
    ply: number;
    lossCp: number;
  }
  const candidates: Candidate[] = [];
  // Comptage non plafonné des coups du joueur par sévérité, dès le balayage
  // (profondeur modérée) : sert de mesure de progression d'une partie à
  // l'autre, indépendante du plafond d'affichage pédagogique (2-3 moments).
  const stats: MistakeStats = { playerMoves: 0, inaccuracies: 0, mistakes: 0, blunders: 0 };
  for (let i = 0; i < game.moves.length; i++) {
    const move = game.moves[i];
    if (move.color !== playerColor) continue;
    stats.playerMoves += 1;
    const before = scoreFromPlayerPov(screened[i].whiteScore, playerColor);
    const after = scoreFromPlayerPov(screened[i + 1].whiteScore, playerColor);
    const lossCp = computeLossCp(before, after);
    const severity = classifySeverity(lossCp);
    if (severity === 'blunder') stats.blunders += 1;
    else if (severity === 'mistake') stats.mistakes += 1;
    else if (severity === 'inaccuracy') stats.inaccuracies += 1;
    if (severity) {
      candidates.push({ moveIndex: i, ply: move.ply, lossCp });
    }
  }
  candidates.sort((a, b) => b.lossCp - a.lossCp);

  // --- Passe 2 : réanalyse en profondeur des meilleurs candidats, dans
  // l'ordre, jusqu'à confirmer au maximum deux moments distincts.
  const moments: KeyMoment[] = [];
  const confirmedPlies: number[] = [];
  let confirmProgress = 0;

  for (const candidate of candidates) {
    if (moments.length >= MAX_MOMENTS_FOR_EXERCISES) break;
    if (confirmedPlies.some((p) => Math.abs(p - candidate.ply) < MIN_PLY_GAP_BETWEEN_MOMENTS)) continue;

    confirmProgress += 1;
    options.onProgress?.({ phase: 'confirming', current: confirmProgress, total: Math.min(candidates.length, 6) });

    const move = game.moves[candidate.moveIndex];
    const fenBefore = fens[candidate.moveIndex];
    const fenAfter = fens[candidate.moveIndex + 1];

    // NB : une seule analyse à la fois sur l'instance moteur partagée (une
    // nouvelle requête annule silencieusement la précédente si elles sont
    // lancées en parallèle) — on les séquence donc explicitement.
    const beforeResult = await engine.analyze(fenBefore, { depth: confirmDepth, timeoutMs, signal: options.signal });
    const afterResult = await engine.analyze(fenAfter, { depth: confirmDepth, timeoutMs, signal: options.signal });

    const beforeTop = beforeResult.bestLines[0];
    const afterTop = afterResult.bestLines[0];
    const beforeWhite = beforeResult.isTerminal ? terminalToWhiteScore(beforeResult, fenBefore) : (beforeTop?.score ?? { type: 'cp' as const, value: 0 });
    const afterWhite = afterResult.isTerminal ? terminalToWhiteScore(afterResult, fenAfter) : (afterTop?.score ?? { type: 'cp' as const, value: 0 });

    const evalBeforePov = scoreFromPlayerPov(beforeWhite, playerColor);
    const evalAfterPov = scoreFromPlayerPov(afterWhite, playerColor);
    const confirmedLossCp = computeLossCp(evalBeforePov, evalAfterPov);
    const severity = classifySeverity(confirmedLossCp);

    if (!severity) {
      // La recherche approfondie ne confirme pas l'erreur : on l'écarte
      // silencieusement (pas de moment fabriqué à la place).
      continue;
    }

    const bestPv = beforeTop?.pvUci ?? [];
    const { san: bestLineSan, firstUci: bestUci } = pvToSan(fenBefore, bestPv);
    const bestSan = bestLineSan[0] ?? move.san;

    const opponentBestPvUci = afterTop?.pvUci;
    const themes = detectThemes({
      fenAfterPlayed: fenAfter,
      opponentBestPvUci,
      evalBeforePlayer: beforeWhite,
      evalAfterPlayedPlayer: afterWhite,
      playerColor,
      lossCp: confirmedLossCp,
    });

    // Même calcul que detectThemes en coulisses (une seule source de vérité) :
    // on le refait ici pour disposer des faits complets (pas seulement des
    // thèmes qui en dérivent), affichés à l'utilisateur comme « la punition ».
    const punishment = computeReplyFacts(fenAfter, opponentBestPvUci, playerColor);

    const explanation = explainMoment({
      playedSan: move.san,
      bestSan,
      bestLineSan,
      themes,
      severity,
      punishment,
    });

    moments.push({
      id: `${game.id}-ply${move.ply}`,
      ply: move.ply,
      fenBefore,
      playedSan: move.san,
      playedUci: move.uci,
      bestSan,
      bestUci: bestUci || bestPv[0] || move.uci,
      bestLineSan,
      evalBeforeCp: toComparableCp(evalBeforePov),
      evalAfterPlayedCp: toComparableCp(evalAfterPov),
      evalAfterBestCp: toComparableCp(evalBeforePov),
      lossCp: confirmedLossCp,
      severity,
      themes,
      playerColor,
      explanation,
      confirmedByReanalysis: true,
      punishment,
    });
    confirmedPlies.push(move.ply);
  }

  const momentsShown = moments.slice(0, MAX_MOMENTS_SHOWN);

  if (moments.length === 0) {
    notes.push(
      candidates.length === 0
        ? "Aucune erreur significative détectée à cette profondeur d'analyse — bravo, rien de flagrant à corriger sur cette partie."
        : "Les candidats détectés lors du balayage initial n'ont pas été confirmés par l'analyse approfondie.",
    );
  } else if (momentsShown.length === 1 && candidates.length > 1) {
    notes.push('Un seul moment a pu être confirmé avec suffisamment de certitude pour être compréhensible et fiable.');
  }

  return {
    gameId: game.id,
    moments: momentsShown,
    momentsForExercises: moments,
    analyzedAt: new Date().toISOString(),
    engineDepth: confirmDepth,
    notes,
    playerColor,
    stats,
  };
}
